import { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isNullish } from 'remeda'
import { ThemeProvider } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'
import { SizePicker } from '@components/SizePicker/SizePicker'
import { lazyImport } from '@helpers'
import { useHeader } from '@velasca/hooks/use-header'
import type { Product, ProductMedia } from '@velasca/types'
import { KlarnaPlacement } from '@velasca/ui/klarna-placement'
import { MediaCarousel } from '@velasca/ui/media-carousel'
import { ProductPage } from '@velasca/ui/product-page'
import { SansText } from '@velasca/ui/sans-text'
import { getThemeByBrand } from '@velasca/utils/get-theme-by-brand'
import { ImagesPageProduct } from '../ImagesPageProduct/ImagesPageProduct'
import { InfoBlockHeaderPresentational } from '../InfoBlockHeader/InfoBlockHeader'
import withLazyLoad from '../LazyLoad'

const { RecentProductsSlider } = lazyImport(
  () => import('@components/RecentProductsSlider/RecentProductsSlider'),
  'RecentProductsSlider',
)

const lazyConfig = {
  rootMargin: `800px 0px 800px 0px`,
  triggerOnce: true,
}

const LazyRecentProductsSlider = withLazyLoad<
  React.ComponentProps<typeof RecentProductsSlider>
>((props) => {
  return (
    <Suspense fallback={null}>
      <RecentProductsSlider {...props} />
    </Suspense>
  )
}, lazyConfig)

interface GiftCardProductPageProps {
  product: Product
  sizesWithDescriptions?: any
  instructions?: any
  moreDetailsInfo?: { description: string }
  sizeGuideSection?: {
    sizeGuide?: any
    miscSettings: any
  }
  productMedia: ProductMedia
  recentProductsSettings?: {
    enabled: boolean
  }
}

const GiftCardProductPage = ({
  product,
  sizesWithDescriptions,
  instructions,
  moreDetailsInfo,
  productMedia,
  recentProductsSettings = {
    enabled: false,
  },
}: GiftCardProductPageProps) => {
  const {
    title,
    handle,
    price,
    rawPrice,
    description,
    medias,
    loading = false,
    pairWithProducts,
    collectionBrandID,
    collectionBrandName,
    productType = '',
    seasonName,
  } = product

  useEffect(() => {
    const apparelProductSkeleton = document.querySelector(
      '#apparel-product-skeleton',
    )

    if (apparelProductSkeleton) {
      apparelProductSkeleton.classList.add('react-loaded')
    }
  }, [])

  const isDesktop = useMediaQuery('(min-width: 992px)')
  const { t } = useTranslation('product')
  const promotionalBannerHeight = useHeader(
    (state) => state.promotionalBannerHeight,
  )
  const navbarHeight = useHeader((state) => state.navbarHeight)

  return (
    <ThemeProvider theme={getThemeByBrand(collectionBrandID)}>
      <ProductPage.MainContainer>
        <ProductPage.MainBlock>
          {isDesktop ? (
            <ProductPage.ImageBlock>
              {medias && (
                <ImagesPageProduct
                  medias={medias}
                  loading={loading}
                  color={description?.color}
                  material={description?.composition}
                  productTitle={title}
                  productType={productType}
                />
              )}
              <ProductPage.ProductSections>
                {instructions?.title && instructions.description && (
                  <ProductPage.StyledDetailsCard
                    {...instructions}
                  ></ProductPage.StyledDetailsCard>
                )}
                {moreDetailsInfo?.description && (
                  <ProductPage.StyledDetailsCard
                    {...moreDetailsInfo}
                    title={t('moreDetailsTitle')}
                  ></ProductPage.StyledDetailsCard>
                )}
              </ProductPage.ProductSections>
            </ProductPage.ImageBlock>
          ) : (
            <MediaCarousel bulletsType="squared" media={productMedia} />
          )}

          <ProductPage.ProductInfoBlock
            headerHeight={promotionalBannerHeight + navbarHeight}
            hasCollectionBrandName={Boolean(collectionBrandName)}
          >
            {handle && !isNullish(price) && (
              <InfoBlockHeaderPresentational
                loading={false}
                collectionBrandName={collectionBrandName}
                description={description}
                handle={handle}
                title={title}
                pairWithTitle={t('pairWithTitle')}
                seasonName={seasonName}
                pairWithText={
                  pairWithProducts && t('pairWithText', pairWithProducts)
                }
              />
            )}
            {sizesWithDescriptions && (
              <SizePicker
                isGiftCard
                product={product}
                title={t('chooseSizeGiftCard')}
                sizes={sizesWithDescriptions.sizes}
                defaultMessage={
                  sizesWithDescriptions.defaultGiftCardDescription
                }
              />
            )}
            {rawPrice ? <KlarnaPlacement amount={rawPrice} /> : null}
            <SansText padding="1rem 2rem">{t('descriptionGiftCard')}</SansText>
          </ProductPage.ProductInfoBlock>

          {!isDesktop && (
            <ProductPage.ProductSections>
              {instructions?.title && instructions.description && (
                <ProductPage.StyledDetailsCard {...instructions} />
              )}
              {moreDetailsInfo?.description && (
                <ProductPage.StyledDetailsCard
                  {...moreDetailsInfo}
                  title={t('moreDetailsTitle')}
                />
              )}
            </ProductPage.ProductSections>
          )}
        </ProductPage.MainBlock>
      </ProductPage.MainContainer>
      <ProductPage.Sliders>
        {recentProductsSettings.enabled && <LazyRecentProductsSlider />}
      </ProductPage.Sliders>
    </ThemeProvider>
  )
}

export default GiftCardProductPage
